import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { MemberPhoto } from '@/components/ui/member-photo';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { cn } from '@/utils/cn';

import { MemberQuickViewDialog } from './member-quick-view-dialog';

export interface MemberNameLinkProps {
  memberId?: string | null;
  name: string;
  positions?: string[];
  photoUrl?: string | null;
  /** When true, a directory thumbnail is shown even if `photoUrl` is empty. */
  showPhoto?: boolean;
  className?: string;
}

const NAME_COLUMN_TO_MEMBER_ID: Record<string, string> = {
  memberName: 'memberId',
  withMemberName: 'withMemberId',
  borrowerMemberName: 'borrowerMemberId',
  giverName: 'memberId',
};

/** Maps a report name column to the member id on the same row, when present. */
export const memberIdForNameColumn = (
  columnKey: string,
  row: Record<string, unknown>,
): string | null => {
  const idKey = NAME_COLUMN_TO_MEMBER_ID[columnKey];
  if (!idKey) return null;
  const value = row[idKey];
  return typeof value === 'string' && value.length > 0 ? value : null;
};

export const canOpenMemberQuickView = (
  membership: ReturnType<typeof useCurrentMembership>,
  isModuleEnabled: (code: string) => boolean,
  memberId?: string | null,
): boolean =>
  Boolean(memberId) &&
  isModuleEnabled('people') &&
  hasPermission(membership, PERMISSIONS.PEOPLE_VIEW);

export const MemberNameLink = ({
  memberId,
  name,
  positions,
  photoUrl,
  showPhoto = false,
  className,
}: MemberNameLinkProps) => {
  const { t } = useTranslation('people');
  const membership = useCurrentMembership();
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const [open, setOpen] = useState(false);

  const display = name.trim();
  const label = display || '—';
  const canOpen = display.length > 0 && canOpenMemberQuickView(membership, isModuleEnabled, memberId);

  const content = (
    <>
      {showPhoto ? <MemberPhoto src={photoUrl} name={label} size="sm" /> : null}
      <span className="min-w-0">
        <span className="text-body-sm">{label}</span>
        {positions && positions.length > 0 ? (
          <span className="mt-0.5 block text-caption text-fg-secondary">{positions.join(', ')}</span>
        ) : null}
      </span>
    </>
  );

  const layoutClass = cn('inline-flex max-w-full items-center gap-2.5 text-left', className);

  if (!canOpen) {
    return <span className={layoutClass}>{content}</span>;
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className={cn(
          layoutClass,
          'rounded-sm text-fg underline-offset-2 hover:text-brand hover:underline',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
        )}
        onClick={handleClick}
        aria-label={t('members.quickView.openAria', { name: display })}
      >
        {content}
      </button>
      <MemberQuickViewDialog
        memberId={memberId ?? null}
        previewName={display}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
};
